import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin, faFacebook, faInstagram, faYoutube } from '@fortawesome/free-brands-svg-icons';
import styles from './Footer.module.css';
import { useHeaderFooter } from '../../context/HeaderFooterContext';

const Footer = () => {
  const { footerElement, headerElement, socialMediaLinks, addresses, copyright, menus, loading } = useHeaderFooter();
  const location = useLocation();

  // Show loading or return null while data is being fetched
  if (loading) {
    return null; // or a loading spinner
  }

  const layout = footerElement?.layout ?? 0;
  const bgColor = footerElement?.bgColorCode || '#ffffff';
  const color = footerElement?.colorCode || '#000000';
  const logoUrl = footerElement?.imageUrl || '';
  const logoLink = footerElement?.logoUrl;

  const getBasePath = () => {
    const parts = (location.pathname || '').split('/').filter(Boolean);
    if (parts[0] === 'Division' && parts[2] === 'Department' && parts[1] && parts[3]) {
      return `/Division/${parts[1]}/Department/${parts[3]}`;
    }
    if (parts[0] === 'Division' && parts[1]) {
      return `/Division/${parts[1]}`;
    }
    // For any other path (including incomplete paths like /Division or /page), default to root
    return '/';
  };

  const getLogoNavigationUrl = () => {
    // First check footer logoUrl
    if (logoLink && logoLink.trim() !== '') {
      return logoLink;
    }
    // If footer logoUrl is empty, check header logoUrl
    if (headerElement?.logoUrl && headerElement.logoUrl.trim() !== '') {
      return headerElement.logoUrl;
    }
    // If both are empty, use getBasePath
    return getBasePath();
  };

  const getSocialIcon = (title) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('linkedin')) return faLinkedin;
    if (lowerTitle.includes('facebook')) return faFacebook;
    if (lowerTitle.includes('instagram')) return faInstagram;
    if (lowerTitle.includes('youtube')) return faYoutube;
    return null;
  };

  const formatAddressLine = (addresses) => {
    const addressMap = {};
    addresses.forEach(addr => {
      addressMap[addr.title.toLowerCase()] = addr.url;
    });

    const lines = [];
    if (addressMap.address) lines.push(addressMap.address);
    if (addressMap.street) lines.push(addressMap.street);
    
    const cityStateZip = [addressMap.city, addressMap.state, addressMap.zip].filter(Boolean).join(', ');
    if (cityStateZip) lines.push(cityStateZip);
    
    if (addressMap['phone number']) lines.push(addressMap['phone number']);

    return lines;
  };

  const addressLines = formatAddressLine(addresses);

  const menuType3 = menus
    .filter(m => m.menuType === 3)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  const menuType4 = menus
    .filter(m => m.menuType === 4)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

  const handleMenuClick = () => {
    // Scroll to top when menu link is clicked
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderMenuLink = (menu) => {
    const label = menu.newTitle || menu.title;
    const href = menu.url || '#';
    const isExternal = /^https?:\/\//i.test(href);
    
    if (isExternal) {
      return (
        <a
          key={menu.id}
          href={href}
          className={styles.menuLink}
          style={{ color }}
          target="_blank"
          rel="noreferrer"
          onClick={handleMenuClick}
        >
          {label}
        </a>
      );
    }
    
    let to = href;
    if (!href.startsWith('/')) {
      const basePath = getBasePath();
      to = basePath === '/' ? `/${href}` : `${basePath}/${href}`;
    }
    
    return (
      <Link
        key={menu.id}
        to={to}
        className={styles.menuLink}
        style={{ color }}
        onClick={handleMenuClick}
      >
        {label}
      </Link>
    );
  };

  // Layout 2 or 4: Left side (logo, social, address), Right side (menus)
  // Layout 3 or 5: Left side (menus), Right side (logo, social, address)
  if (layout === 2 || layout === 4 || layout === 3 || layout === 5) {
    const isReversed = layout === 3 || layout === 5;
    return (
      <footer className={styles.footerRoot}>
        <div className={styles.bar} style={{ background: bgColor, color }}>
          <div className={styles.container}>
            <div className={`${styles.specialMainRow} ${isReversed ? styles.reverseSpecial : ''}`}>
              {/* Left Side: Logo, Social Links, Address (or Menus for layout 3/5) */}
              <div className={styles.leftSection}>
                <Link to={getLogoNavigationUrl()} className={styles.logoWrap} aria-label="Go to homepage">
                  {logoUrl ? (
                    <img className={styles.logoImg} src={logoUrl} alt="Logo" />
                  ) : null}
                </Link>
                
                {socialMediaLinks.length > 0 && (
                  <div className={styles.socialSection}>
                    <p className='h3' style={{ color }}>Social Links</p>
                    <div className={styles.socialLinks}>
                      {socialMediaLinks.map((social, index) => {
                        const icon = getSocialIcon(social.title);
                        if (!icon) return null;
                        
                        const hasValidUrl = social.url && social.url.trim() !== '';
                        
                        if (hasValidUrl) {
                          return (
                            <a
                              key={index}
                              href={social.url}
                              className={styles.socialLink}
                              style={{ color }}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={social.title}
                            >
                              <FontAwesomeIcon icon={icon} />
                            </a>
                          );
                        } else {
                          return (
                            <span
                              key={index}
                              className={styles.socialLink}
                              style={{ color, cursor: 'default' }}
                              aria-label={social.title}
                            >
                              <FontAwesomeIcon icon={icon} />
                            </span>
                          );
                        }
                      })}
                    </div>
                  </div>
                )}

                {/* Address Section */}
                <div className={styles.addressSection}>
                  {addressLines.map((line, index) => (
                    <div key={index} className={styles.addressItem} style={{ color }}>
                      {line}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Side: Menu Columns (or Logo/Social/Address for layout 3/5) */}
              <div className={styles.menuColumns}>
                {menuType3.length > 0 && (
                  <div className={styles.menuColumn}>
                    {menuType3.map(renderMenuLink)}
                  </div>
                )}
                {(layout === 4 || layout === 5) && menuType4.length > 0 && (
                  <div className={styles.menuColumn}>
                    {menuType4.map(renderMenuLink)}
                  </div>
                )}
              </div>
            </div>

            {/* Copyright Section */}
            {copyright && (
              <div className={styles.copyrightRow}>
                <p className={styles.copyright} style={{ color }}>
                  {copyright.url}
                </p>
              </div>
            )}
          </div>
        </div>
      </footer>
    );
  }

  return (
    <>
     <footer className={styles.footerRoot}>
      <div className={styles.bar} style={{ background: bgColor, color }}>
        <div className={styles.container}>
          <div className={`${styles.mainRow} ${layout === 1 ? styles.reverse : ''}`} data-layout={layout}>
            {/* Logo and Social Links Section */}
            <div className={styles.logoSection}>
              <Link to={getLogoNavigationUrl()} className={styles.logoWrap}>
                {logoUrl ? (
                  <img className={styles.logoImg} src={logoUrl} alt="Logo" />
                ) : null}
              </Link>
              
              {socialMediaLinks.length > 0 && (
                <div className={styles.socialSection}>
                  <p className='h3' style={{ color }}>Social Links</p>
                  <div className={styles.socialLinks}>
                    {socialMediaLinks.map((social, index) => {
                      const icon = getSocialIcon(social.title);
                      if (!icon) return null;
                      
                      const hasValidUrl = social.url && social.url.trim() !== '';
                      
                      if (hasValidUrl) {
                        return (
                          <a
                            key={index}
                            href={social.url}
                            className={styles.socialLink}
                            style={{ color }}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={social.title}
                          >
                            <FontAwesomeIcon icon={icon} />
                          </a>
                        );
                      } else {
                        return (
                          <span
                            key={index}
                            className={styles.socialLink}
                            style={{ color, cursor: 'default' }}
                            title={social.title} // Use title instead of aria-label
                          >
                            <FontAwesomeIcon icon={icon} />
                          </span>
                        );
                      }
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Address Section */}
            <div className={styles.addressSection}>
              {addressLines.map((line, index) => (
                <div key={index} className={styles.addressItem} style={{ color }}>
                  {line}
                </div>
              ))}
            </div>
          </div>

          {/* Copyright Section */}
          {copyright && (
            <div className={styles.copyrightRow}>
              <p className={styles.copyright} style={{ color }}>
                {copyright.url}
              </p>
            </div>
          )}
        </div>
      </div>
    </footer>
    </>
   
  );
};

export default Footer;
